import React, { lazy, useEffect, useState } from 'react'

import { pathOr } from 'ramda'
import { useSelector } from 'react-redux'
import { Redirect, Route, Switch, useLocation } from 'react-router-dom'

import { Div, Loader } from '@driftt/tide-core'
import { integrationStatus } from '@driftt/traffic-control'

import AppSubnav from 'components/app/AppSubnav'
import { ChartErrorState } from 'components/core/ChartStates'
import LazyRoute from 'components/core/LazyRoute'
import ProtectedRoute from 'components/core/ProtectedRoute'
import config from 'config'
import { DEFAULT_ROUTES } from 'constants/routes'
import withFilterQueryParams from 'hocs/withFilterQueryParams'
import AvailabilityReport from 'layouts/AvailabilityReport'
import ConversationsReport from 'layouts/ConversationsReport'
import MeetingsReport from 'layouts/MeetingsReport'
import { RevenueReport } from 'layouts/RevenueReport'
import RoutingReport from 'layouts/RoutingReport'
import SiteConciergeMeetings from 'layouts/SiteConciergeMeetings'
import SiteConciergeSearch from 'layouts/SiteConciergeSearch'
import TeamReport from 'layouts/TeamReport'
import { Page } from 'types/pages'
import {
  hasDemoOrgGate,
  hasDriftVoice,
  hasPlaybookReportAccess,
  hasRealTimeDashboard,
  isEmailOnlyOrg,
  showConvoAnalysisInReports,
  userHasChatPlaybookAccess,
  userHasConversationReportsAccess,
  userHasEmailCampaignReportAccess,
  userHasFastlanePlaybookAccess,
  userHasMeetingBookingAppExperience,
  userHasRoutingAccess,
  userHasSalesResultsAccess,
  userHasSearchAppExperience,
  userHasServiceReports,
  userHasSiteConciergeAccess,
  userHasTeamPerformanceAccess,
} from 'utils/access'

import './style.css'

const CallsReport = lazy(() => import('layouts/CallsReport'))
const CallSummaries = lazy(() => import('layouts/CallSummaries'))
const SalesResultsLayout = lazy(() => import('layouts/SalesResults'))
const PlaybooksLayout = lazy(() => import('layouts/Playbooks'))
const SiteConciergeContent = lazy(() => import('layouts/SiteConciergeContent'))
const ConvoAnalysis = lazy(() => import('layouts/ConversationAnalysis'))
const EmailCampaignsReport = lazy(() => import('layouts/EmailCampaigns'))
const RealTimeOverview = lazy(
  () => import('layouts/RealTimeDashboard/Overview')
)
const ServiceLayout = lazy(() => import('layouts/Service'))
const ConversationsDetailReport = lazy(
  () => import('layouts/ConversationsDetailReport')
)

const Pages = () => [
  {
    path: DEFAULT_ROUTES.SITE_CONCIERGE_CONTENT,
    Component: () => <SiteConciergeContent />,
    hasAccess: userHasSiteConciergeAccess(),
  },
  {
    path: DEFAULT_ROUTES.SITE_CONCIERGE_SEARCH,
    Component: () => <SiteConciergeSearch />,
    hasAccess: userHasSearchAppExperience(),
  },
  {
    path: DEFAULT_ROUTES.SITE_CONCIERGE_MEETINGS,
    Component: () => <SiteConciergeMeetings />,
    hasAccess: userHasMeetingBookingAppExperience(),
  },
  {
    path: DEFAULT_ROUTES.PLAYBOOKS,
    Component: () => <PlaybooksLayout />,
    hasAccess: hasPlaybookReportAccess(),
  },
  {
    path: DEFAULT_ROUTES.REAL_TIME,
    Component: () => <RealTimeOverview />,
    hasAccess: hasRealTimeDashboard(),
  },
  {
    path: DEFAULT_ROUTES.SALES_RESULTS,
    Component: () => <SalesResultsLayout />,
    hasAccess: userHasSalesResultsAccess(),
  },
  {
    path: DEFAULT_ROUTES.CONVERSATIONS,
    Component: () => <ConversationsReport />,
    hasAccess: userHasConversationReportsAccess(),
    exact: true,
  },
  {
    path: DEFAULT_ROUTES.MEETINGS,
    Component: () => <MeetingsReport />,
    hasAccess: userHasConversationReportsAccess(),
  },
  {
    path: DEFAULT_ROUTES.CALLS,
    Component: () => <CallsReport />,
    hasAccess: hasDriftVoice(),
  },
  {
    path: DEFAULT_ROUTES.CONVO_ANALYSIS,
    Component: () => <ConvoAnalysis />,
    hasAccess: showConvoAnalysisInReports(),
  },
  {
    path: DEFAULT_ROUTES.TEAM_PERFORMANCE,
    Component: () => <TeamReport />,
    hasAccess: userHasTeamPerformanceAccess(),
  },
  {
    path: DEFAULT_ROUTES.EMAIL_CAMPAIGNS,
    Component: () => <EmailCampaignsReport />,
    hasAccess: userHasEmailCampaignReportAccess(),
  },
  {
    path: DEFAULT_ROUTES.ROUTING,
    Component: () => <RoutingReport />,
    hasAccess: userHasRoutingAccess(),
  },
  {
    path: DEFAULT_ROUTES.TEAM_AVAILABILITY,
    Component: () => <AvailabilityReport />,
    hasAccess: userHasRoutingAccess() || userHasTeamPerformanceAccess(),
  },
  {
    path: DEFAULT_ROUTES.SERVICE,
    Component: () => <ServiceLayout />,
    hasAccess: userHasServiceReports(),
  },
  {
    path: DEFAULT_ROUTES.CONVO_DETAIL,
    Component: () => <ConversationsDetailReport />,
    hasAccess: userHasConversationReportsAccess(),
  },
  {
    path: DEFAULT_ROUTES.REVENUE_OVERVIEW,
    Component: () => <RevenueReport />,
    hasAccess: userHasRoutingAccess(),
  },
  {
    path: DEFAULT_ROUTES.REVENUE_OPPORTUNITIES,
    Component: () => <RevenueReport />,
    hasAccess: userHasRoutingAccess(),
  },
]

const App = () => {
  const [pages, setPages] = useState<Page[]>([])

  useEffect(() => {
    setPages(Pages())
  }, [])
  const getDefaultRedirectPath = (skipSalesPath?: boolean) => {
    const availableRoutes = pages.filter((page) => page.hasAccess)
    const defaultPath =
      availableRoutes.length > 0 ? availableRoutes[0].path : null
    if (isEmailOnlyOrg()) {
      return DEFAULT_ROUTES.EMAIL_CAMPAIGNS
    }
    if (userHasSalesResultsAccess() && !skipSalesPath) {
      if (
        integrationStatus.isIntegrationConnected('salesforce') ||
        hasDemoOrgGate()
      ) {
        return DEFAULT_ROUTES.SALES_RESULTS
      }
      return getDefaultRedirectPath(true)
    }
    return defaultPath
  }
  const mayRedirectToSales = () =>
    userHasSalesResultsAccess() &&
    !isEmailOnlyOrg() &&
    !userHasChatPlaybookAccess() &&
    !userHasFastlanePlaybookAccess()

  const ReportsIndex = () => {
    const { search, pathname } = useLocation()
    const defaultRedirect = getDefaultRedirectPath()
    const integrationsLoaded = useSelector(
      (state) =>
        Object.keys(pathOr({}, ['integrations', 'status'], state)).length !== 0
    )

    if (pathname === '/' && !integrationsLoaded && mayRedirectToSales()) {
      return <Loader />
    }

    if (defaultRedirect) {
      return <Redirect from="/" exact to={defaultRedirect + search} />
    }

    window.location.href = config.APP_HOMEPAGE
    return null
  }

  return pages.length <= 0 ? (
    <Loader />
  ) : (
    <Div className="reports-container">
      <AppSubnav />
      <Div id="reports-body" className="reports-body show-scrollbar">
        <Switch>
          {pages.map((page, index) => (
            <ProtectedRoute key={index} {...page} />
          ))}
          <Route
            path="/not-found"
            component={() => (
              <div style={{ marginTop: '40px' }}>
                <ChartErrorState />
              </div>
            )}
          />

          <Redirect
            path="/siteconcierge"
            to={`${DEFAULT_ROUTES.SITE_CONCIERGE_CONTENT}`}
          />
          <Redirect
            from="/chat-playbooks/:playbookId"
            to={`${DEFAULT_ROUTES.CHAT_PLAYBOOKS}/:playbookId`}
          />
          <Redirect
            from="/email-playbooks/:playbookId"
            to="/reports/playbooks/:playbookId"
          />
          <Redirect
            from="/fast-lane-playbooks/:playbookId"
            to={`${DEFAULT_ROUTES.FASTLANE_PLAYBOOKS}/:playbookId`}
          />

          <Redirect from="/chat-playbooks" to={DEFAULT_ROUTES.CHAT_PLAYBOOKS} />
          <Redirect
            from="/email-playbooks"
            to={DEFAULT_ROUTES.EMAIL_PLAYBOOKS}
          />
          <Redirect
            from="/fast-lane-playbooks"
            to={DEFAULT_ROUTES.FASTLANE_PLAYBOOKS}
          />
          <Redirect from="/automation" to="/" />
          <Route
            path="/conversation-insights"
            render={() => (window.location.href = '/search')}
          />
        </Switch>
        <Route exact path="/" component={ReportsIndex} />
        <LazyRoute
          exact
          path="/call-summary/:id"
          lazyComponent={CallSummaries}
        />
      </Div>
    </Div>
  )
}

export default withFilterQueryParams(App)
